import type { CurrencyAmount } from '@/services/lineItems/lineItemsApi'
import type { ObjectiveType } from '@/services/networks/networkFieldSettings'

/**
 * Delivery caps ("rate limits") on a line item.
 *
 * A cap says "at most this many of this event in this period", either across
 * the whole line item (`GLOBAL`) or per individual user (`PER_USER`, which is
 * what the rest of the industry calls frequency capping). Mirrors
 * `ng.model.RateLimit` in core, which is what the API serialises.
 */

/** Who the cap applies to — the line item as a whole, or one user. */
export type RateLimitScope = 'GLOBAL' | 'PER_USER'
export const RATE_LIMIT_SCOPES: RateLimitScope[] = ['GLOBAL', 'PER_USER']

/** The period the cap's amount is measured over. */
export type RateLimitPeriodType = 'HOURS' | 'DAYS' | 'WEEKS' | 'MONTHS' | 'ALL_TIME'
export const RATE_LIMIT_PERIOD_TYPES: RateLimitPeriodType[] = [
  'HOURS',
  'DAYS',
  'WEEKS',
  'MONTHS',
  'ALL_TIME',
]

/** Cappable fields, in the display order the old UI uses. */
export const RATE_LIMIT_TYPES: ObjectiveType[] = [
  'IMPRESSION',
  'RENDERED_IMPRESSION',
  'VISIBLE_IMPRESSION',
  'VIEWABLE_IMPRESSION',
  'CLICK',
  'CONVERSION',
  'BUDGET',
]

/**
 * Fields a marketplace product may cap. Narrower than a line item's: a product
 * sells impressions and budget, so clicks and conversions are not on offer.
 */
export const AX_PRODUCT_RATE_LIMIT_TYPES: ObjectiveType[] = [
  'IMPRESSION',
  'RENDERED_IMPRESSION',
  'VISIBLE_IMPRESSION',
  'VIEWABLE_IMPRESSION',
  'BUDGET',
]

/** Fields a line item may be capped on when the network has not said otherwise. */
export const DEFAULT_RATE_LIMIT_TYPES: ObjectiveType[] = [
  'IMPRESSION',
  'RENDERED_IMPRESSION',
  'VISIBLE_IMPRESSION',
  'VIEWABLE_IMPRESSION',
  'CLICK',
  'BUDGET',
]

/** Per-user counts are held in a short column server-side. */
export const PER_USER_MAX_AMOUNT = 32767
/** The period multiplier the old UI allows, e.g. "every 2 weeks". */
export const MAX_PERIOD_AMOUNT = 100

export interface RateLimit {
  scope: RateLimitScope
  type: ObjectiveType
  /**
   * A count, except on a `BUDGET` cap, which carries money. Null while a
   * freshly added cap has not been given an amount yet.
   */
  amount: number | CurrencyAmount | null
  periodType: RateLimitPeriodType
  /** How many `periodType` units the period spans. Ignored for `ALL_TIME`. */
  periodAmount: number
  /** Spread the cap evenly over the period instead of spending it as fast as it comes.  */
  smoothed: boolean
}

export function isBudgetLimit(limit: RateLimit): boolean {
  return limit.type === 'BUDGET'
}

/** The numeric part of the amount, whatever kind of cap it is. */
export function rateLimitAmount(limit: RateLimit): number | null {
  if (limit.amount == null) return null
  return typeof limit.amount === 'number' ? limit.amount : limit.amount.amount
}

export function rateLimitCurrency(limit: RateLimit): string | undefined {
  return typeof limit.amount === 'object' && limit.amount ? limit.amount.currency : undefined
}

/** A new cap, ready to be edited: the network's default scope, no amount yet. */
export function newRateLimit(
  type: ObjectiveType,
  scope: RateLimitScope = 'GLOBAL'
): RateLimit {
  const budget = type === 'BUDGET'
  return {
    scope: budget ? 'GLOBAL' : scope,
    type,
    amount: null,
    periodType: budget ? 'DAYS' : 'WEEKS',
    periodAmount: 1,
    // Smoothing only means anything line item-wide.
    smoothed: !budget && scope === 'GLOBAL',
  }
}

/**
 * Like core's `RateLimit.isSameTypeScopePeriod`: two caps the server would
 * treat as the same limit, differing only in amount and smoothing.
 */
export function isSameTypeScopePeriod(a: RateLimit, b: RateLimit): boolean {
  return (
    a.scope === b.scope &&
    a.type === b.type &&
    a.periodType === b.periodType &&
    (a.periodType === 'ALL_TIME' || a.periodAmount === b.periodAmount)
  )
}

/** Why a cap cannot be saved, or nothing when it is fine. */
export type RateLimitProblem = 'amount' | 'amountTooHigh' | 'periodAmount'

export function rateLimitProblem(limit: RateLimit): RateLimitProblem | undefined {
  const amount = rateLimitAmount(limit)
  if (amount == null || !(amount > 0)) return 'amount'
  if (!isBudgetLimit(limit) && limit.scope === 'PER_USER' && amount > PER_USER_MAX_AMOUNT) {
    return 'amountTooHigh'
  }
  if (
    !isBudgetLimit(limit) &&
    limit.periodType !== 'ALL_TIME' &&
    (!(limit.periodAmount >= 1) || limit.periodAmount > MAX_PERIOD_AMOUNT)
  ) {
    return 'periodAmount'
  }
  return undefined
}

export function hasRateLimitProblem(limits: RateLimit[]): boolean {
  return limits.some((limit) => !!rateLimitProblem(limit))
}

/**
 * The caps as the API wants them. A budget cap is always a line item-wide daily
 * spend, so the other fields are forced rather than offered — exactly what the
 * old UI does on submit — and money picks up the line item's currency.
 */
export function rateLimitsForSave(limits: RateLimit[], currency: string): RateLimit[] {
  return limits.map((limit) => {
    if (!isBudgetLimit(limit)) return limit
    return {
      ...limit,
      scope: 'GLOBAL',
      periodType: 'DAYS',
      periodAmount: 1,
      smoothed: false,
      amount: { amount: rateLimitAmount(limit) ?? 0, currency: rateLimitCurrency(limit) || currency },
    }
  })
}

/** Reads the caps off an API payload, filling in anything the response omits. */
export function mapRateLimits(value: unknown): RateLimit[] {
  if (!Array.isArray(value)) return []
  return value.map((raw) => {
    const r = raw as Record<string, unknown>
    return {
      scope: (r.scope as RateLimitScope) ?? 'GLOBAL',
      type: (r.type as ObjectiveType) ?? 'IMPRESSION',
      amount: (r.amount as number | CurrencyAmount | null) ?? null,
      periodType: (r.periodType as RateLimitPeriodType) ?? 'DAYS',
      periodAmount: (r.periodAmount as number) ?? 1,
      smoothed: (r.smoothed as boolean) ?? false,
    }
  })
}
